import { Injectable } from '@angular/core';
import { KeyValue } from '@angular/common';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ComponentType } from '@angular/cdk/overlay';
import { Observable, of, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { Query } from '@services/core-services/models';
import {
  Conditioner,
  FilterEditor,
  FilterEntry,
  FilterGroupType,
  FilterPreview,
  FilterProvider,
  FilterQuery,
  FilterRecord,
  FilterSetup,
  FilterSetupEntry,
  FilterSetupSource,
  FilterSetupSourceFn,
  FilterType,
  Operator,
} from '@services/core-services/filters';
import { FilterPreviewDefaultComponent } from '@enginuity/core/organisms/filtering/viewers/filter-preview-default/filter-preview-default.component';
import { FilterEntryEditorStringComponent } from '@enginuity/core/organisms/filtering/editors/filter-entry-editor-string/filter-entry-editor-string.component';
import { FilterEntryEditorListComponent } from '@enginuity/core/organisms/filtering/editors/filter-entry-editor-list/filter-entry-editor-list.component';
import { FilterEntryEditorDateComponent } from '@enginuity/core/organisms/filtering/editors/filter-entry-editor-date/filter-entry-editor-date.component';
import { FilterEntryEditorBooleanComponent } from '@enginuity/core/organisms/filtering/editors/filter-entry-editor-boolean/filter-entry-editor-boolean.component';
import { FilterEntryEditorNumberComponent } from '@enginuity/core/organisms/filtering/editors/filter-entry-editor-number/filter-entry-editor-number.component';
import { FilterGroup } from '@services/core-services/filters/filter-group';
import { objectToBase64 } from '@services/core-services/utils';
import { DateTime } from 'luxon';

const DEFAULT_LIMIT = 25;
const DEFAULT_PAGE = 1;
const VALUE_SEPARATOR = ',';
const KEY_OPERATOR_SEPARATOR = '.';
const SORT_BY_KEY = 'sort_by';
const NON_FILTER_KEYS = ['limit', 'page', 'search', SORT_BY_KEY, 'view'];

@Injectable({ providedIn: 'root' })
export class FilterService {
  private provider: FilterProvider | undefined;
  private setup: FilterSetup | undefined;

  constructor(private readonly router: Router) {}

  /**
   * @param query The query object to transform
   * @returns A new transformed query object ready for API use
   */
  updateQuery(query: Query | undefined): Query | undefined {
    if (!query) return query;
    const transformedQuery = JSON.parse(JSON.stringify(query));
    if (transformedQuery?.filters?.filters?.length) {
      transformedQuery.filters.filters = transformedQuery.filters.filters
        .map((filter: FilterEntry) => {
          if (filter.operator === Operator.LastX) {
            // Value should be the number X
            const days =
              typeof filter.value === 'number' ? filter.value : parseInt(String(filter.value), 10);
            if (!isNaN(days) && days > 0) {
              // Calculate the UTC date range based on X
              const utcEnd = DateTime.utc().endOf('day');
              const utcStart = utcEnd.minus({ days: days - 1 }).startOf('day');
              const startISO = utcStart.toISO();
              const endISO = utcEnd.toISO();

              if (startISO && endISO) {
                // Return the new filter object for the API call
                return {
                  ...filter,
                  operator: Operator.Between, // Change operator
                  value: [startISO, endISO], // Set value to calculated range
                };
              } else {
                return null; // Example: skip filter
              }
            } else {
              return null; // Example: skip filter
            }
          }
          return filter;
        })
        .filter((f: FilterEntry | null): f is FilterEntry => f !== null); // Filter out nulls from errors
    }

    return transformedQuery;
  }

  /**
   * Method gets filter setup based on underlying filter provider.
   * @param provider
   */
  getSetup(provider: FilterProvider): Observable<FilterSetup> {
    this.provider = provider;
    return this.provider.getSetup().pipe(tap(setup => (this.setup = setup)));
  }

  /**
   * Method gets filter records representing paired setup and rules entries.
   * @param setup
   * @param query
   */
  getRecords(setup: FilterSetup, query: FilterQuery | undefined): FilterRecord[] {
    const rules = (query?.filters || []) as FilterEntry[];
    return rules
      .map(rule => ({
        entry: setup.entries.find(x => x.field === rule.field),
        rule,
      }))
      .filter(record => !record.entry?.hidden) as FilterRecord[];
  }

  /**
   * Gets filter setup grouped by filter setup group.
   * @param setup
   * @param records
   * @param filterSearch
   */
  getGroups(setup: FilterSetup, records: FilterRecord[], filterSearch: string = ''): FilterGroup[] {
    let entries = setup!.entries?.filter(f => !f.hidden && !records.some(x => x.entry === f)) || [];

    if (filterSearch) {
      entries = entries.filter(e => e.label?.toLowerCase().startsWith(filterSearch.toLowerCase()));
    }

    return [
      {
        key: FilterGroupType.Data,
        caption: 'Data',
        icon: 'person',
        cls: 'success-100',
        entries: entries.filter(e => e.group === FilterGroupType.Data),
        count: records.length,
        expanded: true,
      },
      {
        key: FilterGroupType.Attributes,
        caption: 'Attributes',
        icon: 'list',
        cls: 'info-100',
        entries: entries.filter(e => e.group === FilterGroupType.Attributes),
        count: records.length,
      },
      {
        key: FilterGroupType.Events,
        caption: 'Events',
        icon: 'event',
        cls: 'light-100',
        entries: entries.filter(e => e.group === FilterGroupType.Events),
        count: records.length,
      },
    ].filter(g => !!g.entries.length);
  }

  /**
   * Method resolves component type of the preview for the given filter entry.
   * @param entry
   */
  resolvePreviewComponent(entry: FilterSetupEntry): ComponentType<FilterPreview> {
    if (entry.view) return entry.view;
    return FilterPreviewDefaultComponent;
  }

  /**
   * Method resolves component type for the editor for the given filter entry.
   * @param record
   */
  resolveEditorComponent(record: FilterRecord): ComponentType<FilterEditor> | undefined {
    const { entry } = record;
    if (entry.editor) return entry.editor;

    switch (entry.type) {
      case FilterType.Enum:
      case FilterType.Source:
        return FilterEntryEditorListComponent;
      case FilterType.Boolean:
        return FilterEntryEditorBooleanComponent;
      case FilterType.Number:
        return FilterEntryEditorNumberComponent;
      case FilterType.Date:
      case FilterType.DateTime:
        return FilterEntryEditorDateComponent;
      default:
        return FilterEntryEditorStringComponent;
    }
  }

  /**
   * Method resolves filter setup entry source as an Observable
   * @param source
   */
  getSourceOptions(
    source: FilterSetupSource | FilterSetupSourceFn | undefined
  ): Observable<KeyValue<string | number, any>[]> {
    if (!source) return of([]);

    if (Array.isArray(source)) {
      return of(source.map(e => this.toKeyValue(e)));
    } else if (this.isFunction(source)) {
      const result = source() || [];
      return this.isObservable(result)
        ? (result as Observable<FilterSetupSource>).pipe(map(e => e.map(x => this.toKeyValue(x))))
        : of((result as any[]).map(e => this.toKeyValue(e)));
    }

    return of([]);
  }

  requiresValue(record: FilterRecord): boolean {
    return ![Operator.Exists, Operator.NotExists, Operator.IsEmpty, Operator.IsNotEmpty].includes(
      record.rule.operator as Operator
    );
  }

  requiresValueDisplay(record: FilterRecord): boolean {
    return ![Operator.LastX].includes(record.rule.operator as Operator);
  }

  requiresLastValueDisplay(record: FilterRecord): boolean {
    return [Operator.LastX].includes(record.rule.operator as Operator);
  }

  /**
   * Method removes record from the list of records.
   * @param records
   * @param record
   */
  removeRecord(records: FilterRecord[], record: FilterRecord) {
    return records.filter(r => r.entry.field !== record.entry.field);
  }

  /**
   * Navigates to the route with serialized filtering params
   * @param route
   * @param query
   */
  navigate(route: ActivatedRoute, query: Query): Promise<boolean> {
    const qs = this.getSearchFromQuery(query);
    const urlSegments = route.snapshot.pathFromRoot
      .flatMap(route => route.url)
      .map(segment => segment.toString());

    const url = urlSegments.join('/');
    return this.router.navigateByUrl(decodeURI([url, qs].join('?')));
  }

  /**
   * Adds filter rule to the query.
   * @param query
   * @param filterEntry
   */
  addFilterRuleToQuery(query: Query, filterEntry: FilterEntry): Query {
    const { filters, ...rest } = query || {};
    const { filters: filterRules, condition } = filters || {};
    return { filters: { condition, filters: (filterRules || []).concat([filterEntry]) }, ...rest };
  }

  /**
   * Gets Query object by passing query string.
   * @param params
   */
  getQueryFromSearch(params: Params): Observable<Query> {
    return of(this.searchParamsToQuery(params));
  }

  /**
   * Serializes query to the query params string.
   * @param query
   */
  getSearchFromQuery(query: Query): string {
    const queryParams = new URLSearchParams();
    if (query.page && query.page > 1)
      queryParams.set('page', (query.page || DEFAULT_PAGE).toString());

    if (query.limit) queryParams.set('limit', (query.limit || DEFAULT_LIMIT).toString());

    if (query.sort_by) {
      queryParams.set('sort_by', this.parseSortBy(query.sort_by));
    }

    if (query.search) {
      queryParams.set('search', query.search);
    }

    (query.filters?.filters || []).forEach((rule: any) => {
      const entry = rule as FilterEntry;
      const name = entry.operator ? `${entry.field}.${entry.operator}` : entry.field;

      if (entry.type === 'events') {
        queryParams.set(name, this.serializeEventValue(entry));
      } else if (entry.operator === Operator.LastX) {
        // For LastX, serialize the number directly
        queryParams.set(name, String(entry.value));
      } else {
        // For others, serialize the value (string or string array)
        queryParams.set(name, this.serializeValue(entry.value));
      }
    });

    return decodeURIComponent(queryParams.toString());
  }

  /**
   * Converts Params to filter Query.
   * @param params
   */
  private searchParamsToQuery(params: Params): Query {
    if (!params || !Object.keys(params)?.length) return {} as Query;
    const query: Query = { filters: { condition: Conditioner.AND, filters: [] } };
    for (const key in params) {
      if (Object.prototype.hasOwnProperty.call(params, key)) {
        const value = params[key];

        if (key === SORT_BY_KEY) {
          this.isValidParamValue(value) && (query[key] = this.deserializeSortBy(value));
        } else if (this.isNonFilterKey(key)) {
          // @ts-expect-error - Dynamically assigning known keys is safe here
          this.isValidParamValue(value) && (query[key] = this.parseValue(value));
        } else {
          const { key: k, operator } = this.parseSearchParamKey(key as string);
          const entry = this.setup?.entries.find(e => e.field === k);
          this.isValidParamValue(value) &&
            query.filters?.filters!.push({
              type: entry?.group || FilterGroupType.Data,
              field: k,
              operator,
              value: this.parseValue(value, entry?.type),
            });
        }
      }
    }

    return query;
  }

  /**
   * Serializes order by property of the Query.
   * @param sortBy
   */
  private parseSortBy(sortBy: Record<string, any>) {
    return Object.entries(sortBy)
      .map(([key, value]) => `${key}.${value}`)
      .join(VALUE_SEPARATOR);
  }

  /**
   * Deserializes order part of the query params into Query order property.
   * @param param
   */
  private deserializeSortBy(param: string): Record<string, any> {
    return param.split(',').reduce(
      (acc, pair) => {
        const [key, value] = pair.split('.');
        acc[key] = value;
        return acc;
      },
      {} as Record<string, any>
    );
  }

  /**
   * Serializes value
   * @param value
   */
  private serializeValue(value: any): string {
    // Handle array (likely date range) by joining, otherwise convert to string
    return Array.isArray(value) ? value.join(VALUE_SEPARATOR) : String(value);
  }

  /**
   * Serializes events value
   * @param entry
   */
  private serializeEventValue(entry: FilterEntry) {
    return decodeURIComponent(objectToBase64(entry.value));
  }

  /**
   * Parses value from URL query param string
   * @param value
   * @param entryType Optional entry type to determine how to parse the value
   */
  private parseValue(
    value: string | null | undefined,
    entryType?: string
  ): string | string[] | number {
    if (value === null || value === undefined) {
      return ''; // Or handle as needed, maybe return undefined?
    }

    const stringValue = String(value);

    // Check if it contains the separator for array values
    if (stringValue.includes(VALUE_SEPARATOR)) {
      return stringValue.split(VALUE_SEPARATOR);
    }

    // If entry type is 'string', always return as string, don't convert to number
    if (entryType === 'string') {
      return stringValue;
    }

    // Attempt to parse as number if it looks like one
    // Use a regex to check if it's purely numeric (integer or float)
    if (/^-?\d+(\.\d+)?$/.test(stringValue)) {
      const num = parseFloat(stringValue);
      if (!isNaN(num)) {
        return num;
      }
    }

    // Otherwise, return the original string value
    return stringValue;
  }

  /**
   * Maps array or KeyValue to KeyValue.
   * @param e
   * @private
   */
  private toKeyValue(e: any): KeyValue<string | number, any> {
    return {
      key: e.key || e,
      value: e.value || e,
    };
  }

  /**
   * Detects if param value is valid one.
   * @param value
   */
  private isValidParamValue(value: any) {
    return value !== null && value !== undefined && value !== '';
  }

  /**
   * Checks if given property is a function.
   * @param prop
   * @private
   */
  private isFunction(prop: any) {
    return prop && typeof prop === 'function';
  }

  /**
   * Checks if a given property is observable.
   * @param prop
   * @private
   */
  private isObservable(prop: unknown): prop is { subscribe: (...args: any[]) => any } {
    return (
      typeof prop === 'object' && prop !== null && typeof (prop as any).subscribe === 'function'
    );
  }

  /**
   * Checks if key is in the list of non-filter keys.
   * @param key
   */
  private isNonFilterKey(key: string) {
    return NON_FILTER_KEYS.indexOf(key) > -1;
  }

  /**
   * Parses search params and gets key an operator object.
   * @param param
   */
  parseSearchParamKey(param: string): { key: string; operator: Operator } {
    const parts = param.split(KEY_OPERATOR_SEPARATOR);
    // Find the string key (e.g., 'LastX') corresponding to the value (e.g., 'last')
    const opKey = Object.entries(Operator).find(([, val]) => val === parts[1])?.[0] as
      | keyof typeof Operator
      | undefined;
    return {
      key: parts[0],
      // Use the found key to access the enum value, default to Equals if not found
      operator: opKey ? Operator[opKey] : Operator.Equals,
    };
  }

  compare(a: FilterEntry[], b: FilterEntry[]): boolean {
    if (a.length !== b.length) {
      return false;
    }

    const sortedArr1 = a.slice().sort(this.compareEntries);
    const sortedArr2 = b.slice().sort(this.compareEntries);

    for (let i = 0; i < sortedArr1.length; i++) {
      if (!this.areEntriesEqual(sortedArr1[i], sortedArr2[i])) {
        return false;
      }
    }

    return true;
  }

  private compareEntries(a: FilterEntry, b: FilterEntry): number {
    return (a.type.localeCompare(b.type) ||
      a.field.localeCompare(b.field) ||
      a.operator?.localeCompare(b.operator!))!;
  }

  private areEntriesEqual(a: FilterEntry, b: FilterEntry): boolean {
    return (
      a.type === b.type &&
      a.field === b.field &&
      a.operator === b.operator &&
      a.value.toString() == b.value.toString()
    );
  }
}
